import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import Lottie from 'lottie-react';
import RoundedInput from "../UI/Cards/Automations/EditAutomations/RoundedInput"
import classes from "../UI/Cards/Automations/EditAutomations/ScheduledAction.module.css"
import Button2 from '../UI/Button/Button2';
import { createScheduleApi } from '../../Api Methods/Api';
import loadingAnim from '../../assets/animations/newLoading.json';
import successAnim from '../../assets/animations/success.json';
import { Button } from 'primereact/button';

const OUTPUT_PIN_NAMES = {
    "5": "Output 1", "6": "Output 2", "13": "Output 3", "19": "Output 4",
    "25": "Output 5", "26": "Output 6", "27": "Output 7", "28": "Output 8",
};

const CreateMassScheduleUI = ({ selectedOutputs, selectedSites, apiData, onClose }) => {

    const dispatch = useDispatch();

    const selectedOutputNames = (selectedOutputs || [])
        .map(pin => OUTPUT_PIN_NAMES[pin])
        .filter(Boolean)
        .join(", ");

    const [daysSelected, setDaysSelected] = useState(["0", "1", "2", "3", "4", "5", "6"]);
    const [isAutoArmed, setIsAutoArmed] = useState(true);
    const [hour, setHour] = useState('12');
    const [minutes, setMinutes] = useState('00');
    const [editTime, setEditTime] = useState(false);

    // Loading/done state
    const [isLoading, setIsLoading] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [currentSiteIndex, setCurrentSiteIndex] = useState(0);
    const [successCount, setSuccessCount] = useState(0);
    const [statusMessageFail, setStatusMessageFail] = useState("");

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const handleCreate = async () => {
        const time = HandlePrepareTime();
        const action = HandlePrepareAction();
        const pin = selectedOutputs;
        const days = daysSelected;

        setIsLoading(true);
        setIsDone(false);
        setSuccessCount(0);
        setStatusMessageFail("");
        setCurrentSiteIndex(0);

        let localSuccessCount = 0;
        let localFailMessages = "";

        for (let i = 0; i < selectedSites.length; i++) {
            const site = selectedSites[i];
            setCurrentSiteIndex(i + 1);

            const siteName = site.tag && site.tag.trim() !== '' ? site.tag : site.key;

            const body = {
                deviceUUID: site.uuid,
                days,
                time,
                action,
                pin,
                active: 1,
                reminder: 0,
            };

            try {
                const response = await createScheduleApi(apiData, body, dispatch);
                if (response && response.success) {
                    localSuccessCount++;
                } else {
                    localFailMessages = localFailMessages ? `${localFailMessages}\nFailed for ${siteName}` : `Failed for ${siteName}`;
                }
            } catch (err) {
                localFailMessages = localFailMessages ? `${localFailMessages}\nFailed for ${siteName}` : `Failed for ${siteName}`;
            }

            await delay(700);
        }

        setSuccessCount(localSuccessCount);
        setStatusMessageFail(localFailMessages);
        setIsLoading(false);
        setIsDone(true);
    };

    const HandlePrepareTime = () => {
        if (hour === "00" && minutes === "00") {
            return "23:59";
        }
        return hour + ":" + minutes;
    };

    const HandlePrepareAction = () => {
        return isAutoArmed ? "Arm" : "Disarm";
    };

    const HandleOnDayClicked = (dayIndex) => {
        const isDayAlreadyClicked = daysSelected.find(day => day === dayIndex);
        if (isDayAlreadyClicked) {
            removeItem(dayIndex);
        } else {
            addItem(dayIndex);
        }
    };

    const addItem = (dayIndex) => {
        setDaysSelected(prev => [...prev, dayIndex]);
    };

    const removeItem = (dayIndex) => {
        setDaysSelected(prev => prev.filter(day => day !== dayIndex));
    };

    const Days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    useEffect(() => {
        if (hour === "" || hour == null) {
            setHour("12");
        }

        if (minutes === "" || minutes == null) {
            setMinutes("00");
        }

        const hourAsString = hour.toString();
        const minutesAsString = minutes.toString();

        if (hourAsString.length < 2) {
            setHour("0" + hour);
        }

        if (minutesAsString.length < 2) {
            setMinutes("0" + minutes);
        }

        if (hourAsString === "00" && minutesAsString === "00") {
            setHour("23");
            setMinutes("59");
        }
    }, [editTime]);

    const handleChangeHour = (event) => {
        if (event.target.value === "") return;
        const newValue = event.target.value;
        if (IsValueValid(newValue) && IsHourValueValid(newValue)) {
            setHour(newValue);
        }
    };

    const handleChangeMinutes = (event) => {
        if (event.target.value === "") return;
        const newValue = event.target.value;
        if (IsValueValid(newValue) && IsMinuteValueValid(newValue)) {
            setMinutes(newValue);
        }
    };

    const IsValueValid = (value) => /^\d*$/.test(value) && value.length <= 2;
    const IsHourValueValid = (value) => value >= 0 && value <= 23;
    const IsMinuteValueValid = (value) => value >= 0 && value < 60;

    if (isLoading) {
        return (
            <div className={classes.verticalFlex}>
                <h3 style={{ whiteSpace: "pre-line", color: "#858585", textAlign: "center", fontSize: "20px", fontWeight: 600 }}>
                    {'Creating permanent schedules'}
                </h3>
                <p style={{ whiteSpace: "pre-line", color: "#858585", textAlign: "center", fontSize: "18px", fontWeight: 550 }}>
                    {'Please wait...'}
                </p>

                <Lottie animationData={loadingAnim} loop style={{ width: 250, height: 250 }} />

                <p style={{ whiteSpace: "pre-line", color: "#858585", textAlign: "center", fontSize: "18px", fontWeight: 550 }}>
                    {`Site ${currentSiteIndex} of ${selectedSites.length} complete`}
                </p>
            </div>
        );
    }

    if (isDone) {
        return (
            <div className={classes.verticalFlex}>
                <h3 style={{ color: "#858585", textAlign: "center", fontSize:"20px" }}>
                    {`Complete!`}
                </h3>

                 <Lottie animationData={successAnim} loop={false} style={{ width: 200, height: 200 }} />

                <h3 style={{ color: "#858585", textAlign: "center", fontWeight:550}}>
                    {`Permanent schedules successfully created for ${successCount} site${successCount !== 1 ? "s" : ""}`}
                </h3>

                {statusMessageFail && (
                    <h3 style={{ whiteSpace: "pre-line", color: "#d9534f", textAlign: "center" }}>
                        {statusMessageFail}
                    </h3>
                )}

                <br/>
                <Button label='Close' severity='secondary' onClick={onClose} />   
            </div>
        );
    }

    return (
        <div>
            <div className={classes.verticalFlex}>
                <div className={classes.verticalFlex}>

                    <h4 style={{ margin: "0px", fontWeight: "600", fontSize:"18px", width: "fit-content", color:"#6e6e6e" }} >{selectedOutputNames}</h4>
                     <p>will be</p>
                    <div className={classes.horizontalFlex} >
                        <Button2 style={{ minWidth: "110px", backgroundColor: isAutoArmed && "var(--Primary-Color)", color: isAutoArmed && "white" }} onClick={() => setIsAutoArmed(true)}>{"Auto Armed"}</Button2>
                        <Button2 style={{ minWidth: "110px", backgroundColor: !isAutoArmed && "var(--Primary-Color)", color: !isAutoArmed && "white" }} onClick={() => setIsAutoArmed(false)}>{"Auto Disarmed"}</Button2>
                    </div>
                    <p style={{margin:0}}>at</p>
                    {!editTime
                        ? <h2 onClick={() => setEditTime(prev => !prev)} style={{ margin: "5.8px", fontWeight: "500", cursor: "pointer", color: "#6e6e6e", fontSize: "30px" }}>{hour + ":" + minutes}</h2>
                        : <div className={classes.horizontalFlex} style={{ position: "relative" }}>
                            <RoundedInput value={hour} onChange={handleChangeHour} placeholder={hour || "12"} />
                            :
                            <RoundedInput value={minutes} onChange={handleChangeMinutes} placeholder={minutes || "00"} />
                            <div style={{ position: "absolute", left: "450px", bottom: "30px", textAlign: "center", borderRadius: "50%", border: "1px solid grey", width: "20px", height: "fit-content", cursor: "pointer" }} onClick={() => setEditTime(false)}>X</div>
                        </div>
                    }
                </div>

                <div className={classes.verticalFlex}>
                    <h4 style={{ margin: "0px", fontWeight: "400", textAlign: "center", color: "#6e6e6e" }}>Repeat:</h4>
                    <ul className={classes.horizontalFlex} style={{ flexWrap: "wrap" }}>
                        {["0", "1", "2", "3", "4", "5", "6"].map((dayIndex, index) => (
                            <li key={index}>
                                <Button2
                                    style={{ backgroundColor: daysSelected.find(day => day === dayIndex) && "var(--Primary-Color)", color: daysSelected.find(day => day === dayIndex) && "white", minWidth: "60px", minHeight: "30px" }}
                                    onClick={() => HandleOnDayClicked(dayIndex)}
                                >
                                    {Days[dayIndex]}
                                </Button2>
                            </li>
                        ))}
                    </ul>
                    <h4 style={{ marginTop: "10px", fontWeight: "400", width: "fit-content", color:"#6e6e6e" }} >Unselect days you do not want schedule to run</h4>
                </div>

                <div className={classes.verticalFlex} style={{ gap: "15px" }}>
                    <Button2
                        style={{ minWidth: "300px", marginTop: "20px" }}
                        disabled={daysSelected.length === 0}
                        onClick={handleCreate}
                    >
                        {"Create schedule"}
                    </Button2>
                </div>
            </div>
        </div>
    );
};

export default CreateMassScheduleUI;
